/**
 * 로고 경로 관련 유틸리티 함수
 * 프로바이더 로고와 캐릭터 이미지 경로를 관리
 */

import { getProvider } from '../config/providers.config';
import config from '../config';

/**
 * 프로바이더 로고 경로 가져오기
 * @param {string} providerName - 프로바이더 이름
 * @returns {string} 로고 이미지 경로
 */
export function getProviderLogoPath(providerName) {
  const provider = getProvider(providerName);
  
  if (!provider) {
    return '/img/default-logo.svg';
  }
  
  // 새로운 경로 구조 사용
  const filename = provider.logoFile.replace('vender-logo-', '');
  const logoPath = `/img/vendor-logo/${filename}`;
  
  return logoPath;
}

/**
 * 프로바이더 캐릭터 이미지 경로 가져오기
 * @param {string} providerName - 프로바이더 이름
 * @param {string} type - 페이지 타입 ('slot' 또는 'casino')
 * @returns {string|null} 캐릭터 이미지 경로 또는 null
 */
export function getProviderCharacterPath(providerName, type = 'slot') {
  const provider = getProvider(providerName);
  
  if (!provider) {
    return null;
  }
  
  // characterFile이 정의되어 있으면 API 경로 사용
  if (provider.characterFile) {
    return resolveApiImageUrl(provider.characterFile);
  }
  
  // 캐릭터 파일명 생성 (기존 패턴 유지)
  const normalizedName = provider.id;
  return `/img/provider-characters/${type}-${normalizedName}.png`;
}

/**
 * 프로바이더 한글 이름 가져오기
 * @param {string} providerName - 프로바이더 이름
 * @returns {string} 한글 이름
 */
export function getProviderKoreanName(providerName) {
  const provider = getProvider(providerName);
  return provider ? provider.koreanName : providerName;
}

/**
 * 프로바이더 표시 이름 가져오기
 * @param {string} providerName - 프로바이더 이름
 * @returns {string} 표시 이름
 */
export function getProviderDisplayName(providerName) {
  const provider = getProvider(providerName);
  return provider ? provider.displayName : providerName;
}

/**
 * 레거시 호환성을 위한 파일명 가져오기
 * 기존 providerUtils.js의 getProviderFileName과 호환
 * @param {string} providerName - 프로바이더 이름
 * @returns {string} 파일명 (확장자 제외)
 */
export function getProviderFileName(providerName) {
  const provider = getProvider(providerName);
  
  if (provider && provider.logoFile) {
    // 'vender-logo-' 접두사와 '.png' 확장자 제거
    return provider.logoFile
      .replace('vender-logo-', '')
      .replace('.png', '');
  }
  
  // 프로바이더를 찾을 수 없는 경우 기존 로직 사용
  return providerName.toLowerCase().replace(/['\s]/g, '');
}

/**
 * API에서 반환한 이미지 경로를 절대 URL로 변환
 * @param {string} apiPath - API에서 반환한 상대 경로 (예: /api/providers/logos/pragmatic.png)
 * @returns {string} 절대 URL (프로토콜에 따라 적절한 URL 생성)
 */
export function resolveApiImageUrl(apiPath) {
  // 이미 절대 URL인 경우 그대로 반환
  if (!apiPath) return '/img/default-logo.svg';
  if (apiPath.startsWith('http://') || apiPath.startsWith('https://')) {
    return apiPath;
  }
  
  // 현재 프로토콜 감지 (HTTPS or HTTP)
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  
  // Admin API (포트 5100)를 사용해야 하는 경로들
  // - /uploads/: 모든 업로드 파일 (vendor-logos, logos 등)
  // - /api/providers/: 프로바이더 이미지 API
  
  // /images/ 경로는 정적 파일이므로 그대로 반환
  if (apiPath.startsWith('/images/')) {
    return apiPath;
  }

  // HTTPS 환경에서는 상대 경로 사용 (nginx 프록시 처리)
  if (protocol === 'https:') {
    if (apiPath && (apiPath.includes('/uploads/') || apiPath.includes('/providers/'))) {
      // HTTPS에서는 상대 경로로 처리 (nginx가 프록시)
      return apiPath;
    }
    // 기타 API 경로도 상대 경로로 처리
    return apiPath.startsWith('/') ? apiPath : '/' + apiPath;
  }
  
  // HTTP 개발 환경
  const adminUrl = `${protocol}//${hostname}:5100`;
  
  if (apiPath && (apiPath.includes('/uploads/') || apiPath.includes('/providers/'))) {
    // /uploads/로 시작하는 경우 그대로 사용
    if (apiPath.startsWith('/uploads/')) {
      return adminUrl + apiPath;
    }
    // /api/providers/로 시작하는 경우
    if (apiPath.startsWith('/api')) {
      return adminUrl + apiPath;
    }
    // 그 외의 경우 /api 추가
    const cleanPath = apiPath.startsWith('/') ? apiPath : '/' + apiPath;
    return adminUrl + '/api' + cleanPath;
  }
  
  // 다른 API 요청은 기존대로 user API (포트 5101) 사용
  const apiUrl = config.API_URL || `${protocol}//${hostname}:5101/api`;
  const baseUrl = apiUrl.replace(/\/api$/, '');
  
  // apiPath가 /api로 시작하면 그대로 붙이고, 아니면 /api 추가
  if (apiPath.startsWith('/api')) {
    return baseUrl + apiPath;
  }
  
  // /로 시작하지 않으면 / 추가
  const cleanPath = apiPath.startsWith('/') ? apiPath : '/' + apiPath;
  return baseUrl + '/api' + cleanPath;
}